import { Cohorts, AdminMatches } from '@platform/client';
import { keepPreviousData } from '@tanstack/react-query';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import {
  AdminMatchExportResponse,
  MatchGroupData,
  MatchInitiationTypeEnum,
  MatchProgressEnum,
  MatchStatusEnum,
} from '@platform/client/schemas';
import { useMemo, useState, useContext } from 'react';
import { AdminTable, AdminTableContext } from '../../AdminTable';
import { AdminListPageWrapper } from '../../AdminListPage';
import { CreateIndividualMatchModal } from './NewIndividualMatchModal';
import { CreateTeamMatchModal } from './NewTeamsMatchModal';
import { ProjectLink } from '../projects/ProjectsAdmin';
import { OrganizationLink } from '../organizations/OrganizationsAdmin';
import { ProfessionalLink } from '../professionals/ProfessionalsAdmin';
import { Link } from 'wouter';
import { ADMIN_ROUTES, ROUTES } from '../../../routes/consts';
import { ImportMatchesModal } from './ImportMatchesModal';

const columnHelper = createColumnHelper<MatchGroupData>();

export const MatchesAdmin = () => {
  return (
    <AdminListPageWrapper>
      <MatchesAdminContent />
    </AdminListPageWrapper>
  );
};

const MatchesAdminContent = () => {
  const { adminQueryOptionsV2 } = useContext(AdminTableContext);
  const [createIndividualMatchModalOpen, setCreateIndividualMatchModalOpen] = useState(false);
  const [createTeamMatchModalOpen, setCreateTeamMatchModalOpen] = useState(false);
  const [importMatchesModalOpen, setImportMatchesModalOpen] = useState(false);
  const {
    data: newMatchData,
    isError,
    isLoading,
    isSuccess,
  } = AdminMatches.useGetAdminMatches(
    { ...adminQueryOptionsV2 },
    {
      query: {
        placeholderData: keepPreviousData,
      },
    },
  );

  const adminMatches = newMatchData?.matches || [];
  const adminMatchesCount = newMatchData?.filtered_count || 0;

  const { data: cohortFilterData } = Cohorts.useGetCohorts();

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor('id', {
          cell: (matchGroupId) => (
            <MatchGroupLink
              matchGroupId={matchGroupId.getValue()}
              matchGroupName={matchGroupId.getValue().toString()}
            />
          ),
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>ID</span>,
          maxSize: 10,
          enableColumnFilter: true,
          meta: {
            filterVariant: 'text',
          },
          enableSorting: true,
        }),
        columnHelper.accessor('initiation_type', {
          id: 'initiation_types',
          cell: (initiation_type) => <div>{initiation_type.getValue()}</div>,
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Sent By</span>,
          size: 25,
          enableColumnFilter: true,
          meta: {
            filterVariant: 'multi-select',
            filterSelectOptions: Object.values(MatchInitiationTypeEnum).map((type) => ({
              label: type,
              value: type,
            })),
          },
        }),
        columnHelper.accessor('professionals', {
          id: 'professional_name',
          cell: (professionals) => (
            <div className='flex gap-2 flex-wrap'>
              {professionals.getValue().map((member, index) => {
                const name = member.firstname + ' ' + member.lastname;
                const isLast = index === professionals.getValue().length - 1;
                return (
                  <span key={member.id}>
                    <ProfessionalLink userId={member.id} name={name} />
                    {!isLast && ','}
                  </span>
                );
              })}
            </div>
          ),
          header: () => (
            <span className='overflow-ellipsis whitespace-nowrap'>Professional(s)</span>
          ),
          size: 25,
          enableColumnFilter: true,
          meta: {
            filterVariant: 'text',
          },
          enableSorting: false,
        }),
        columnHelper.accessor('cohort', {
          id: 'cohort_names',
          cell: (cohort) => <div>{cohort.getValue()?.name}</div>,
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Cohort</span>,
          size: 25,
          enableColumnFilter: true,
          meta: {
            filterVariant: 'multi-select',
            filterSelectOptions: cohortFilterData?.cohorts.map((cohort) => ({
              label: cohort.name,
              value: cohort.name,
            })),
          },
        }),
        columnHelper.accessor('representative', {
          id: 'representative_name',
          cell: (representative) => (
            <p>{representative.getValue().firstname + ' ' + representative.getValue().lastname}</p>
          ),
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Representative</span>,
          size: 25,
          enableColumnFilter: true,
          meta: {
            filterVariant: 'text',
          },
        }),
        columnHelper.accessor('organization', {
          id: 'organization_name',
          cell: (organization) => (
            <OrganizationLink
              organizationId={organization.getValue().id}
              organizationName={organization.getValue().name}
            />
          ),
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Organization</span>,
          size: 125,
          enableColumnFilter: true,
          meta: {
            filterVariant: 'text',
          },
        }),
        columnHelper.accessor('project', {
          id: 'project_name',
          cell: (project) => (
            <ProjectLink
              projectId={project.getValue()?.id}
              projectName={project.getValue()?.name}
            />
          ),
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Project Name</span>,
          size: 150,
          enableColumnFilter: true,
          meta: {
            filterVariant: 'text',
          },
        }),
        columnHelper.accessor('status', {
          id: 'statuses',
          cell: (info) => `match ${info.getValue() == 'matched' ? 'confirmed' : info.getValue()}`,
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Match Status</span>,
          size: 25,
          enableSorting: false,
          enableColumnFilter: true,
          meta: {
            filterVariant: 'multi-select',
            filterSelectOptions: Object.values(MatchStatusEnum).map((status) => ({
              label: status,
              value: `match ${status}`,
            })),
          },
        }),
        columnHelper.accessor('progress', {
          id: 'progresses',
          cell: (info) => info.getValue(),
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Match Progress</span>,
          size: 25,
          enableColumnFilter: true,
          meta: {
            filterVariant: 'multi-select',
            filterSelectOptions: Object.values(MatchProgressEnum).map((progress) => ({
              label: progress,
              value: progress,
            })),
          },
        }),
      ] as ColumnDef<MatchGroupData>[],
    [cohortFilterData],
  );

  return (
    <>
      <AdminTable
        columns={columns}
        data={adminMatches}
        isSuccess={isSuccess}
        isLoading={isLoading}
        isError={isError}
        filteredCount={adminMatchesCount}
        enableFiltering={true}
        tableActions={[
          {
            text: 'Create Individual Match',
            callback: () => setCreateIndividualMatchModalOpen(true),
          },
          {
            text: 'Create Team Match',
            callback: () => setCreateTeamMatchModalOpen(true),
          },
          {
            text: 'Import Matches',
            callback: () => setImportMatchesModalOpen(true),
          },
        ]}
        exportOptions={{
          exportQuery: AdminMatches.useGetAdminMatchExport,
          dataToExportFn: (data: AdminMatchExportResponse) => data.matches,
          exportFileName: `matching_export_${new Date().toISOString().replace(' ', '_')}.csv`,
        }}
      />
      {createIndividualMatchModalOpen && (
        <CreateIndividualMatchModal onClose={() => setCreateIndividualMatchModalOpen(false)} />
      )}
      {createTeamMatchModalOpen && (
        <CreateTeamMatchModal onClose={() => setCreateTeamMatchModalOpen(false)} />
      )}
      <ImportMatchesModal
        isOpen={importMatchesModalOpen}
        onClose={() => setImportMatchesModalOpen(false)}
      />
    </>
  );
};

export const MatchGroupLink = ({
  matchGroupId,
  matchGroupName,
}: {
  matchGroupId: number;
  matchGroupName: string;
}) => {
  if (matchGroupId && matchGroupName) {
    return (
      <Link
        to={`~/platform${ROUTES.ADMIN}${ADMIN_ROUTES.MATCHES}/${matchGroupId}`}
        className='text-brand-500 hover:underline'
      >
        {matchGroupName}
      </Link>
    );
  }
};
